import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Send, Loader2, MessageCircle, Lightbulb, AlertCircle } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { usePreferences } from '../context/PreferencesContext';

interface Message {
  id: string;
  content: string;
  isSystem: boolean;
  isOwn: boolean;
  createdAt: string;
}

interface BuddyStatus {
  status: 'none' | 'matching' | 'paired';
  pairId?: string;
  trackSlug?: string;
}

interface WeeklyPrompts {
  currentPrompt: string;
  allPrompts: string[];
}

export default function BuddyChat() {
  const { preferences } = usePreferences();
  const { fetchApi } = useApi();
  const [status, setStatus] = useState<BuddyStatus | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [prompts, setPrompts] = useState<WeeklyPrompts | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  async function loadData() {
    try {
      const [statusRes, messagesRes, promptsRes] = await Promise.all([
        fetchApi<BuddyStatus>('/api/buddy/status'),
        fetchApi<{ messages: Message[] }>('/api/buddy/messages').catch(() => ({ messages: [] })),
        fetchApi<WeeklyPrompts>('/api/buddy/prompts').catch(() => null)
      ]);

      setStatus(statusRes);
      setMessages(messagesRes.messages || []);
      setPrompts(promptsRes);

      if (statusRes.status !== 'paired') {
        setError('You need to be paired with a buddy to chat');
      }
    } catch (err) {
      console.error('Failed to load buddy chat:', err);
      setError('Failed to load chat');
    } finally {
      setLoading(false);
    }
  }

  function scrollToBottom() {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }

  async function handleSend(e: React.FormEvent) {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    const content = newMessage.trim();
    setNewMessage('');

    // Optimistic update
    const tempMessage: Message = {
      id: `temp-${Date.now()}`,
      content,
      isSystem: false,
      isOwn: true,
      createdAt: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempMessage]);

    try {
      const response = await fetchApi<{ success: boolean; message: Message }>('/api/buddy/messages', {
        method: 'POST',
        body: JSON.stringify({ content })
      });

      // Replace temp message with real one
      setMessages(prev =>
        prev.map(m => (m.id === tempMessage.id ? response.message : m))
      );
    } catch (err) {
      // Remove failed message
      setMessages(prev => prev.filter(m => m.id !== tempMessage.id));
      setError('Failed to send message');
    } finally {
      setSending(false);
    }
  }

  function usePrompt(prompt: string) {
    setNewMessage(prompt);
  }

  if (preferences.connection_mode !== 'buddy') {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <MessageCircle className="w-16 h-16 text-gray-600 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-white mb-2">Buddy Chat</h1>
        <p className="text-gray-400 mb-6">
          Enable Accountability Buddy mode in your preferences to chat with a learning partner.
        </p>
        <Link
          to="/preferences"
          className="inline-flex items-center gap-2 px-6 py-3 bg-accent-blue hover:bg-accent-blue/80 text-slate-900 rounded-lg font-medium transition-colors"
        >
          Go to Preferences
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
      </div>
    );
  }

  if (status?.status !== 'paired') {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <MessageCircle className="w-16 h-16 text-gray-600 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-white mb-2">No Buddy Yet</h1>
        <p className="text-gray-400 mb-6">
          You'll be able to chat once you're matched with a buddy.
        </p>
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 px-6 py-3 bg-accent-blue hover:bg-accent-blue/80 text-slate-900 rounded-lg font-medium transition-colors"
        >
          Back to Dashboard
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto flex flex-col h-[calc(100vh-12rem)]">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link
          to="/dashboard"
          className="p-2 text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-green-400" />
            Buddy Chat
          </h1>
          <p className="text-sm text-gray-400">
            Messages with your accountability buddy
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300">
            &times;
          </button>
        </div>
      )}

      {/* Weekly Prompt Card */}
      {prompts && (
        <div className="mb-4 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
          <div className="flex items-start gap-3">
            <Lightbulb className="w-5 h-5 text-yellow-400 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-yellow-400 mb-1">This week's prompt</p>
              <p className="text-white">{prompts.currentPrompt}</p>
              <button
                onClick={() => usePrompt(prompts.currentPrompt)}
                className="mt-2 text-sm text-yellow-400 hover:text-yellow-300 transition-colors"
              >
                Use this prompt
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto bg-gray-800/30 rounded-lg p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <p>No messages yet. Say hello to your buddy!</p>
          </div>
        ) : (
          messages.map(message => (
            <div
              key={message.id}
              className={`flex ${message.isOwn ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`
                  max-w-[80%] px-4 py-2 rounded-lg
                  ${message.isSystem
                    ? 'bg-gray-700/50 text-gray-400 text-sm italic'
                    : message.isOwn
                      ? 'bg-accent-blue text-slate-900'
                      : 'bg-gray-700 text-white'
                  }
                `}
              >
                <p>{message.content}</p>
                <p className={`text-xs mt-1 ${message.isOwn ? 'text-blue-200' : 'text-gray-500'}`}>
                  {new Date(message.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <form onSubmit={handleSend} className="mt-4 flex gap-2">
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Type a message..."
          className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-accent-blue focus:outline-none"
          maxLength={2000}
        />
        <button
          type="submit"
          disabled={!newMessage.trim() || sending}
          className="px-4 py-3 bg-accent-blue hover:bg-accent-blue/80 disabled:bg-gray-700 disabled:cursor-not-allowed text-slate-900 rounded-lg transition-colors"
        >
          {sending ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Send className="w-5 h-5" />
          )}
        </button>
      </form>
    </div>
  );
}
